/* eslint-disable no-param-reassign */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { TypeOfTableData } from '../../pricing-simulator/types/generic-table.model';
import { filterColumnsForToggleView } from '../../pricing-simulator/utils/column-utils';
import { Scenario } from '../../scenarios/types/scenario.model';
import {
  ISimulatorHomeCard,
  ISimulatorHomeState
} from '../types/scenario-state';
import MODULE_TO_PAGE_MAP from '../types/simulation-module-to-page-map';
import {
  getAccessConfigForScenario,
  getListOfScenariosForTable,
  getModuleSequence,
  getDynamicBreadcrumbs,
  getOpportunityToColorMap,
  getScenarioDetails
} from './simulator-home-async-thunks';

const MODULE_CARDS: ISimulatorHomeCard[] = [
  {
    title: 'Planner',
    description:
      "Visualise the existing PPG's and SKU's in a 2x2 matrix for different KPI's to define white spaces and opportunities.",
    imageUrl: '/static/images/Planner.svg',
    name: MODULE_TO_PAGE_MAP.PLANNER,
    link: '/srm/simulator/:id/planner'
  },
  {
    title: 'Pricing And Distribution',
    description:
      "Change the KPI's for pricing and distribution for an existing PPG / SKU at manufacturer level and refine the pricing at retailer level",
    imageUrl: '/static/images/Pricing.svg',
    link: '/srm/simulator/:id/pricing-distribution',
    name: MODULE_TO_PAGE_MAP.PRICING
  },
  {
    title: 'Renovation',
    description:
      "Reconstruct an existing SKU's attributes in terms of weight, packaging and claims.",
    imageUrl: '/static/images/Renovation.svg',
    link: '/srm/simulator/:id/renovation',
    name: MODULE_TO_PAGE_MAP.RENOVATION
  },
  {
    title: 'Innovation',
    description:
      "Launch of an SKU / PPG by mapping it to brand, channel and specifying KPI's for pricing and distribution.",
    imageUrl: '/static/images/Innovation.svg',
    link: '/srm/simulator/:id/innovation',
    name: MODULE_TO_PAGE_MAP.INNOVATION
  },
  {
    title: 'Delist',
    description:
      'Removal of an SKU either from the portfolio or at a channel/retailer.',
    imageUrl: '/static/images/Delist.svg',
    link: '/srm/simulator/:id/delist',
    name: MODULE_TO_PAGE_MAP.DELIST
  },
  {
    title: 'Simulation Summary',
    description: "View the impact of the changes on the financial KPI's",
    imageUrl: '/static/images/Summary.svg',
    link: '/srm/simulator/:id/summary',
    name: MODULE_TO_PAGE_MAP.SUMMARY
  }
];

const initialState: ISimulatorHomeState = {
  isFetchingModuleSequence: false,
  isFetchingDynamicBreadcrumbs: false,
  isFetchingScenarios: false,
  scenarioList: {},
  inputColsForScenario: [],
  cardsSequence: MODULE_CARDS.slice(),
  moduleSequence: [],
  dynamicBreadcrumbs: [],
  currentScenario: null,
  moduleNameToModuleIdMap: {
    [MODULE_TO_PAGE_MAP.PLANNER_HOME]: null,
    [MODULE_TO_PAGE_MAP.PLANNER]: null,
    [MODULE_TO_PAGE_MAP.PRICING]: null,
    [MODULE_TO_PAGE_MAP.DELIST]: null,
    [MODULE_TO_PAGE_MAP.INNOVATION]: null,
    [MODULE_TO_PAGE_MAP.SUMMARY]: null,
    [MODULE_TO_PAGE_MAP.RENOVATION]: null,
    [MODULE_TO_PAGE_MAP.POTENTIAL_RISK]: null,
    [MODULE_TO_PAGE_MAP.SCENARIO_COMPARE]: null
  },
  moduleAccessMap: {
    [MODULE_TO_PAGE_MAP.PLANNER_HOME]: {},
    [MODULE_TO_PAGE_MAP.PLANNER]: {},
    [MODULE_TO_PAGE_MAP.PRICING]: {},
    [MODULE_TO_PAGE_MAP.DELIST]: {},
    [MODULE_TO_PAGE_MAP.INNOVATION]: {},
    [MODULE_TO_PAGE_MAP.SUMMARY]: {},
    [MODULE_TO_PAGE_MAP.SIMPLIFICATION]: {},
    [MODULE_TO_PAGE_MAP.RENOVATION]: {}
  },
  opportunityToColorMap: [
    {
      pk_id: 1,
      color: '#80EDFC',
      label: 'Price Change',
      value: 'Price Change',
      is_applicable_to_child: false
    },
    {
      pk_id: 2,
      color: '#B98C52',
      label: 'Distribution',
      value: 'Distribution',
      is_applicable_to_child: false
    },
    {
      pk_id: 3,
      color: '#EA9988',
      label: 'Renovation',
      value: 'Renovation',
      is_applicable_to_child: false
    },
    {
      pk_id: 4,
      color: '#393939',
      label: 'Delist',
      value: 'Simplification',
      is_applicable_to_child: false
    },
    {
      pk_id: 5,
      color: '#FFDC00',
      label: 'Innovation',
      value: 'Innovation',
      is_applicable_to_child: false
    }
  ],
  isFetchingOpportunityToColorMap: false
};

const simulatorHomeSlice = createSlice({
  name: 'simulatorHome',
  initialState,
  reducers: {
    setScenario(state, action: PayloadAction<Scenario>) {
      state.currentScenario = action.payload;
    }
  },
  extraReducers: (builder) =>
    builder
      .addCase(getModuleSequence.pending, (state) => {
        state.isFetchingModuleSequence = true;
      })
      .addCase(getModuleSequence.rejected, (state) => {
        state.isFetchingModuleSequence = false;
      })
      .addCase(getModuleSequence.fulfilled, (state, action) => {
        state.isFetchingModuleSequence = false;
        const { moduleSequence, moduleNameToModuleIdMap } = action.payload;
        state.moduleSequence = moduleSequence.slice();
        state.moduleNameToModuleIdMap = moduleNameToModuleIdMap;
        state.cardsSequence = moduleSequence
          .sort((moduleA, moduleB) => moduleA.order - moduleB.order)
          .map((module) => {
            const card = MODULE_CARDS.find(
              (moduleCard) => moduleCard.name === module.module.name
            );
            if (card) {
              return { ...card, title: module.module.alias };
            }
            console.log(`Card with name ${module.module.name} not found`);
            return null;
          })
          .filter((module) => module);
      })
      .addCase(getDynamicBreadcrumbs.pending, (state) => {
        state.isFetchingDynamicBreadcrumbs = true;
      })
      .addCase(getDynamicBreadcrumbs.rejected, (state) => {
        state.isFetchingDynamicBreadcrumbs = false;
      })
      .addCase(getDynamicBreadcrumbs.fulfilled, (state, action) => {
        state.isFetchingDynamicBreadcrumbs = false;
        state.dynamicBreadcrumbs = action.payload;
      })
      .addCase(getOpportunityToColorMap.pending, (state) => {
        state.isFetchingOpportunityToColorMap = true;
      })
      .addCase(getOpportunityToColorMap.rejected, (state) => {
        state.isFetchingOpportunityToColorMap = false;
      })
      .addCase(getOpportunityToColorMap.fulfilled, (state, action) => {
        state.opportunityToColorMap = action.payload.slice();
        state.isFetchingOpportunityToColorMap = false;
      })
      .addCase(getScenarioDetails.pending, (state) => {
        state.isFetchingModuleSequence = true;
      })
      .addCase(getScenarioDetails.rejected, (state) => {
        state.isFetchingModuleSequence = false;
      })
      .addCase(getScenarioDetails.fulfilled, (state, action) => {
        const { scenarioAccessConfig, scenarioDetails } = action.payload;
        state.currentScenario = scenarioDetails;
        state.moduleAccessMap = scenarioAccessConfig;
        state.isFetchingModuleSequence = false;
      })
      .addCase(getListOfScenariosForTable.pending, (state) => {
        state.isFetchingScenarios = true;
      })
      .addCase(getListOfScenariosForTable.rejected, (state) => {
        state.isFetchingScenarios = false;
      })
      .addCase(getListOfScenariosForTable.fulfilled, (state, action) => {
        state.scenarioList = action.payload;
        state.inputColsForScenario = filterColumnsForToggleView(
          [TypeOfTableData.DISPLAY, TypeOfTableData.INPUT],
          action.payload.header,
          ''
        );
        state.isFetchingScenarios = false;
      })
      .addCase(getAccessConfigForScenario.fulfilled, (state, action) => {
        state.moduleAccessMap = action.payload;
      })
});

export const { setScenario } = simulatorHomeSlice.actions;

export { getModuleSequence, getDynamicBreadcrumbs };

export default simulatorHomeSlice;
